import React, { useState } from "react";
import {
	Box,
	Typography,
	TextField,
	Button,
	InputAdornment,
	Link,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { useFirebase } from "../context/ApiContext";
import { useSnackbar } from "notistack";

const ForgotPasswordPage = () => {
	const navigate = useNavigate();
	const { forgotPassword } = useFirebase();
	const { enqueueSnackbar } = useSnackbar();
	const [email, setEmail] = useState("");
	const [loading, setLoading] = useState(false);
	const [sent, setSent] = useState(false);
	const [error, setError] = useState("");

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");
		const trimmed = (email || "").trim();
		if (!trimmed) {
			setError("Introduce tu correo electrónico.");
			return;
		}
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(trimmed)) {
			setError("Introduce un correo electrónico válido.");
			return;
		}
		setLoading(true);
		try {
			await forgotPassword(trimmed);
			setSent(true);
		} catch (err) {
			const msg =
				err?.response?.data?.error ||
				err?.message ||
				"Error al enviar el enlace. Inténtalo de nuevo.";
			setError(msg);
			enqueueSnackbar(msg, { variant: "error" });
		} finally {
			setLoading(false);
		}
	};

	return (
		<Box
			sx={{
				minHeight: "100vh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
				p: 3,
			}}
		>
			<Box
				sx={{
					maxWidth: 440,
					width: "100%",
					bgcolor: "#fff",
					borderRadius: "24px",
					p: 4,
					boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
				}}
			>
				<Link
					component="button"
					onClick={() => navigate("/")}
					sx={{
						display: "inline-flex",
						alignItems: "center",
						gap: 0.5,
						mb: 3,
						color: "#64748b",
						fontSize: 14,
						textDecoration: "none",
						"&:hover": { color: "#6366F1" },
						cursor: "pointer",
					}}
				>
					<ArrowBackIcon sx={{ fontSize: 20 }} />
					Volver
				</Link>

				{sent ? (
					<Box sx={{ textAlign: "center" }}>
						<Typography
							sx={{
								fontSize: 22,
								fontWeight: 700,
								color: "#1a1a2e",
								mb: 1,
								fontFamily: "'Inter', sans-serif",
							}}
						>
							Revisa tu correo
						</Typography>
						<Typography
							sx={{
								fontSize: 14,
								color: "#64748b",
								mb: 3,
								lineHeight: 1.6,
								fontFamily: "'Inter', sans-serif",
							}}
						>
							Si existe una cuenta con ese email, recibirás un enlace para
							restablecer la contraseña. Revisa también la carpeta de spam.
						</Typography>
						<Button
							onClick={() => navigate("/")}
							sx={{
								height: 48,
								borderRadius: "14px",
								background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
								color: "#fff!important",
								textTransform: "none",
								fontWeight: 600,
								fontSize: 14,
								px: 4,
								"&:hover": {
									background:
										"linear-gradient(135deg, #5558E3 0%, #7C4DE8 100%)",
								},
							}}
						>
							Volver
						</Button>
					</Box>
				) : (
					<>
						<Typography
							sx={{
								fontSize: 22,
								fontWeight: 700,
								color: "#1a1a2e",
								mb: 0.5,
								fontFamily: "'Inter', sans-serif",
							}}
						>
							¿Olvidaste tu contraseña?
						</Typography>
						<Typography
							sx={{
								fontSize: 14,
								color: "#64748b",
								mb: 3,
								lineHeight: 1.6,
								fontFamily: "'Inter', sans-serif",
							}}
						>
							Introduce tu correo y te enviaremos un enlace para restablecerla.
						</Typography>
						<form onSubmit={handleSubmit}>
							<TextField
								fullWidth
								label="Correo electrónico"
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								error={!!error}
								helperText={error}
								placeholder="tu@email.com"
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<EmailIcon sx={{ color: "text.secondary" }} />
										</InputAdornment>
									),
								}}
								sx={{
									mb: 2,
									"& .MuiOutlinedInput-root": { borderRadius: "12px" },
								}}
							/>
							<Button
								type="submit"
								fullWidth
								disabled={loading}
								sx={{
									height: 48,
									borderRadius: "12px",
									background:
										"linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
									color: "#fff!important",
									textTransform: "none",
									fontWeight: 600,
									fontSize: 14,
									"&:hover": {
										background:
											"linear-gradient(135deg, #5558E3 0%, #7C4DE8 100%)",
									},
								}}
							>
								{loading ? "Enviando…" : "Enviar enlace"}
							</Button>
						</form>
					</>
				)}
			</Box>
		</Box>
	);
};

export default ForgotPasswordPage;
